import React, { useCallback, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { styled } from 'styled-components';
import { Select } from 'antd';
import { DefaultOptionType } from 'rc-select/lib/Select';
import { EntityListResponse, EntityListSourceType, EntityListTypeName } from '@ydistri/api-sdk';
import {
  ConfigurationFieldStatus,
  SearchableSelectOptionType,
  selectFilterHandler,
} from '@ydistri/utils';
import EntityListSelectOption from '../../../screens/Configuration/ConfigurationCategories/SegmentPotentialDefinition/ConfigurationEntityLists/EntityListSelectOption';
import { TypeLoader } from './TypeLoader';
import ConfigurationMultiselect from '../ConfigurationMultiselect/ConfigurationMultiselect';
import type { MultiselectCustomerPartialEntity } from '../ConfigurationMultiselect/configurationMultiselectTypes';
import { buildEntityListMultiselectData } from './entityListSelectLib';

// Use non-generic Select here to support both single and multiple value modes
const StyledSelect = styled(Select)<{ $width?: CSSProperties['width'] }>`
  width: ${p => p.$width ?? '100%'};
  height: 32px !important;

  /* Align styling more closely with ConfigurationSelect (height and item alignment) */
  .ant-select-selector {
    min-height: 32px !important; /* matches the 'normal' size used in ConfigurationSelect */
  }

  .ant-select-selection-item-content {
    align-self: center;
  }

  .ant-select-selection-overflow .ant-select-selection-overflow-item .ant-select-selection-item {
    min-height: 24px;
  }
`;

type EntityListSelectProps = {
  types: EntityListTypeName[];
  sourceType?: EntityListSourceType;
  skipEntityListIds?: number[];
  disabled?: boolean;
  width?: CSSProperties['width'];
  loading?: boolean;
  allowClear?: boolean;
  hideArchivedOptions?: boolean;
} & (
  | {
      multiselect: true;
      onChange: (value: number[]) => void;
      value?: number[];
    }
  | {
      multiselect: false;
      onChange: (value: number) => void;
      value?: number;
    }
);

const getEntityListSearchableString = (entityList: EntityListResponse): string =>
  [
    entityList.entityListId.toString(),
    entityList.name,
    entityList.customerEntityListId,
    entityList.description,
    entityList.entityListSourceTypeId,
    ...entityList.entityListTypes.map(t => t.entityListTypeName),
  ]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .join(' ');

/**
 * Loads entity lists per type and aggregates them into a single select.
 * Note: underlying hook supports only single type, thus a loader sub-component is used per type.
 */
const EntityListSelect: React.FC<EntityListSelectProps> = ({
  types,
  sourceType,
  skipEntityListIds = [],
  onChange,
  disabled = false,
  width = '100%', //'50rem',
  value,
  loading,
  multiselect,
  allowClear = false,
  hideArchivedOptions = false,
}) => {
  const [listsByType, setListsByType] = useState<
    Partial<Record<EntityListTypeName, Record<number, EntityListResponse>>>
  >({});
  const [anyLoading, setAnyLoading] = useState(false);

  const upsertLists = useCallback((type: EntityListTypeName, lists: EntityListResponse[]) => {
    setListsByType(prev => {
      const current = prev[type] ?? {};
      const updated = { ...current };
      lists.forEach(l => {
        updated[l.entityListId] = l;
      });
      return { ...prev, [type]: updated };
    });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setAnyLoading(prev => (loading ? true : prev && false));
  }, []);

  const selectedEntityListIds = useMemo(() => {
    if (multiselect) {
      return new Set(Array.isArray(value) ? value : []);
    }
    return new Set(typeof value === 'number' ? [value] : []);
  }, [multiselect, value]);

  const listsBySelectedType = useMemo(() => {
    const filteredListsByType: Partial<Record<EntityListTypeName, EntityListResponse[]>> = {};

    types.forEach(type => {
      const listsMap = listsByType[type];
      if (!listsMap) return;
      filteredListsByType[type] = Object.values(listsMap)
        .filter(l => !skipEntityListIds.includes(l.entityListId))
        .filter(
          l => !hideArchivedOptions || !l.isArchived || selectedEntityListIds.has(l.entityListId),
        )
        .filter(l => !l.isDataDeleted || selectedEntityListIds.has(l.entityListId))
        .sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0);
    });

    return filteredListsByType;
  }, [hideArchivedOptions, listsByType, selectedEntityListIds, skipEntityListIds, types]);

  const options: DefaultOptionType[] = useMemo(() => {
    // Build grouped options by provided types order
    const groups: DefaultOptionType[] = [];
    types.forEach(type => {
      const lists = listsBySelectedType[type];
      if (!lists) return;
      const items: SearchableSelectOptionType[] = lists.map(l => ({
        id: l.entityListId,
        value: l.entityListId,
        label: <EntityListSelectOption entityList={l} type="compact" />,
        searchableString: getEntityListSearchableString(l),
      }));
      if (items.length > 0) {
        groups.push({ label: type, options: items });
      }
    });
    return groups;
  }, [listsBySelectedType, types]);

  const multiselectData: MultiselectCustomerPartialEntity[] = useMemo(() => {
    const listsById = new Map<number, EntityListResponse>();

    types.forEach(type => {
      listsBySelectedType[type]?.forEach(list => {
        listsById.set(list.entityListId, list);
      });
    });

    return buildEntityListMultiselectData(Array.from(listsById.values()));
  }, [listsBySelectedType, types]);

  const handleChange = useCallback(
    (val: unknown) => {
      if (multiselect) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- double check
        onChange((Array.isArray(val) ? val : []) as number[]);
      } else {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions,@typescript-eslint/non-nullable-type-assertion-style -- double check
        onChange((typeof val === 'number' ? val : null) as number);
      }
    },
    [multiselect, onChange],
  );

  const effectiveLoading = loading ?? anyLoading;
  const computedDisabled = disabled || effectiveLoading || options.length === 0;
  const placeholder = (() => {
    if (computedDisabled) return 'No lists available';
    return multiselect ? 'Select list(s)' : 'Select list';
  })();
  const multiselectValue = Array.isArray(value) ? value : [];

  return (
    <>
      {types.map(t => (
        <TypeLoader
          key={t}
          type={t}
          sourceType={sourceType}
          onLoaded={upsertLists}
          onLoading={setLoading}
        />
      ))}
      {multiselect ? (
        <ConfigurationMultiselect
          data={multiselectData}
          options={options}
          value={multiselectValue}
          onValueChange={onChange}
          disabled={computedDisabled}
          loading={effectiveLoading}
          placeholder={placeholder}
          width={width}
          size="normal"
          status={ConfigurationFieldStatus.DEFAULT}
          popupMatchSelectWidth={false}
          maxTagCount="responsive"
        />
      ) : (
        <StyledSelect
          options={options}
          $width={width}
          onChange={handleChange}
          value={value}
          allowClear={allowClear}
          showSearch={true}
          filterOption={selectFilterHandler}
          popupMatchSelectWidth={false}
          loading={effectiveLoading}
          disabled={computedDisabled}
          placeholder={placeholder}
        />
      )}
    </>
  );
};

export default EntityListSelect;
